// External libraries
import { isFunction, isObject, isString } from '@checkout/react-components';

// Internal modules
import { buildUrl, getParams } from './url';
import { $logger } from '../logger';
import { getLocale, getToken } from '../globals';
import { appStore } from '../../store';

/**
 * @function buildMPPUrl
 * @param {object} additionalParams
 * @param {string} mppPath
 * @returns {string}
 * @description Use to create a url to MPP app
 */
const buildMPPUrl = ({ params = {}, mppPath }) => {
  const origin = 'https://www.paypal.com';
  const { country, locale } = getLocale();
  const adjustedPath = mppPath.replace(/^\//, '');
  const additionalParams = Object.assign({}, params, { 'locale.x': locale });

  return buildUrl(`${origin}/${country}/webapps/mpp/${adjustedPath}`, {
    additionalParams,
    withCurrentParams: false,
  });
};

const TERMS_FUNCTIONS = {
  DEFAULT: ({ path }) => buildMPPUrl({ mppPath: path }),
  feeInformation: () => {
    const { country, language, locale } = getLocale();
    // eslint-disable-next-line max-len
    return `https://www.paypalobjects.com/digitalassets/c/website/ua/pdf/${country}/${language}/defid.pdf?locale.x=${locale}`;
  },
  generateHelpLink: () => {
    const { locale } = getLocale();
    return `https://www.paypal.com/selfhelp/home/?locale.x=${locale}`;
  },
};

/**
 * @function buildTermsUrl
 * @param {string} config
 * @returns {string}
 */
const buildTermsUrl = config => {
  const { useFunction = 'DEFAULT' } = config;
  let buildUrlFn = TERMS_FUNCTIONS[useFunction];

  if (!isFunction(buildUrlFn)) {
    $logger.warn('terms_link_function_not_found', config);
    buildUrlFn = TERMS_FUNCTIONS.DEFAULT;
  }

  return buildUrlFn(config);
};

const fetchJson = (...args) => {
  return fetch(...args).then(result => {
    try {
      return result.json();
    } catch (e) {
      $logger.error('weasley_error_parsing_json_response', {
        message: e.message,
      });
      throw e;
    }
  });
};

/**
 * @function filterObject
 * @param {object} obj
 * @param {function} predicate
 * @returns {object}
 */
const DEFAULT_PREDICATE = ([key, value]) => value;
const filterObject = (obj, predicate = DEFAULT_PREDICATE) => {
  if (!isObject(obj)) {
    $logger.error('cannot_filter_non_object', {
      type: typeof obj,
    });
  }

  if (predicate && !isFunction(predicate)) {
    $logger.warn('filter_object_filter_function_is_not_a_function', {
      type: typeof predicate,
    });
    return obj;
  }

  return Object.entries(obj)
    .filter(predicate)
    .reduce(
      (accum, [key, value]) => ({
        ...accum,
        [key]: value,
      }),
      {},
    );
};

/**
 * @function once
 * @param {function} fn | Function to restrict to single invocation
 * @returns {function}
 */
const once = fn => {
  let hasBeenCalled = false;
  return (...args) => {
    if (!hasBeenCalled) {
      hasBeenCalled = true;
      return fn(...args);
    }
  };
};

const focusOnFirstError = state => {
  const form = document.forms[0];

  if (!form) {
    return;
  }

  const fields = Array.from(form.elements);
  const fieldWithError = fields
    .map(field => field.id)
    .find(field => state[field]?.displayError);

  if (!fieldWithError) {
    return;
  }

  const el = document.getElementById(fieldWithError);
  if (el) {
    el.focus();

    /**
     * `setSelectionRange()` only works for inputs of type 'password',
     * 'search', 'tel', 'text', and 'url'. Chrome will throw an error
     * if you try to use setSelectionRange on another type of input,
     * such as 'email'
     */
    const isInput = el.tagName === 'INPUT';
    const hasSelectionRange = /^password|search|tel|text|url$/i.test(el.type);
    if (isInput && hasSelectionRange) {
      const { length } = el.value;
      el.setSelectionRange(length, length);
    }
  }
};

/**
 * @function getAddressMetaKey
 * @param {string} country
 * @returns {string}
 * @description Takes an optional country code string and returns the key
 * where the address metadata is stored in the api store. We were originally
 * using the locale code as the key but the locale code isn't always the
 * language_country, e.g. IN can be en_GB. Also, we don't want people to be
 * confused by the format and think it is the locale so we are purposefully not
 * using the language_country format and instead using country~language
 */
const getAddressMetaKey = country => {
  const { country: countryCode, language } = getLocale();
  const metaKey = `${country || countryCode}~${language}`;

  return metaKey;
};

/**
 * @function getObjectProperty
 * @param {object} obj
 * @param {string} path
 * @param {object} config
 * @returns {*}
 */
const getObjectProperty = (obj, path = '', config = {}) => {
  const { defaultValue, splitOn = '.' } = config;

  if (!isObject(obj) || !isString(splitOn) || !isString(path) || !path.length) {
    return;
  }

  const pathArray = path.split(splitOn);
  let current = obj[pathArray.shift()];

  while (
    current &&
    pathArray.length &&
    (isObject(current) || Array.isArray(current))
  ) {
    current = current[pathArray.shift()];
  }

  return pathArray.length || current === undefined ? defaultValue : current;
};

const getClientMetadataID = defaultID => {
  const urlParams = getParams();

  return (
    window.xprops?.clientMetadataID ||
    urlParams['client-metadata-id'] ||
    defaultID ||
    getToken()
  );
};

/**
 * @function camelToSnakeCase
 * @param {string} s
 * @returns {string}
 * @description Takes a camel case or pascal case string and returns a snake
 * case string. Useful for transforming camel/pascal strings for logging.
 */
const REG_EXP = /(?!^)([A-Z])/g;
const camelToSnakeCase = s => s.replace(REG_EXP, '_$&').toLowerCase();

const noop = () => {};

const getShippingCountry = () => {
  const { form: formState } = appStore.getState();
  const { shipToBilling, shippingAddress } = formState;
  return shipToBilling.value
    ? getLocale().country
    : shippingAddress.country.value;
};

export {
  buildMPPUrl,
  buildTermsUrl,
  camelToSnakeCase,
  fetchJson,
  filterObject,
  focusOnFirstError,
  getAddressMetaKey,
  getClientMetadataID,
  getObjectProperty,
  getShippingCountry,
  noop,
  once,
};
